// components/MobileSpecials.jsx
import { memo, useRef, useState, useEffect } from 'react';
import { useFilter } from '../../contexts/FilterContext';
import { useConfig } from '../../contexts/ConfigContext';
import Special from '../Products/Special';

// Using memo to prevent unnecessary rerenders
const MobileSpecials = memo(({ specialsList, setSelectedSpecialId, defaultDesktopSpecials = false, desktopSpecialsTitle = '' }) => {
    const { setSelectedFilters, menuFilter, activeFilterCount } = useFilter();
    const { menuConfig = {} } = useConfig();
    const { specialsName } = menuConfig;
    const safeSpecialsName = specialsName || 'Deals';
    const scrollContainerRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [showScrollButtons, setShowScrollButtons] = useState(false);

    // Determine if component should render
    const shouldRender = specialsList && specialsList.length > 0 && activeFilterCount === 0;

    const handleViewAllSpecials = () => {
        // Enable specials filtering to show all specials
        setSelectedFilters(prev => ({ ...menuFilter, ...prev, specials: true }));
    };

    // Check scroll position and container overflow
    const updateScrollButtons = () => {
        if (!scrollContainerRef.current) return;

        const container = scrollContainerRef.current;
        const { scrollLeft, scrollWidth, clientWidth } = container;

        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        setShowScrollButtons(scrollWidth > clientWidth);
    };

    // Smooth scroll function
    const scrollTo = (direction) => {
        if (!scrollContainerRef.current) return;

        const container = scrollContainerRef.current;
        const scrollAmount = 300; // Adjust scroll distance
        const newScrollLeft = direction === 'left'
            ? container.scrollLeft - scrollAmount
            : container.scrollLeft + scrollAmount;

        container.scrollTo({
            left: newScrollLeft,
            behavior: 'smooth'
        });
    };

    // Handle mouse wheel horizontal scrolling (mobile only)
    const handleWheel = (e) => {
        if (!scrollContainerRef.current) return;

        // Check if we're on a mobile/touch device
        const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        
        // Only allow wheel scrolling on mobile devices
        if (!isMobile) {
            return; // Exit early on desktop - no wheel scrolling allowed
        }

        // Only prevent default if we're actually scrolling horizontally
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
            e.preventDefault();
            scrollContainerRef.current.scrollLeft += e.deltaX;
        } else if (e.deltaY !== 0) {
            // Convert vertical scroll to horizontal on mobile
            e.preventDefault();
            scrollContainerRef.current.scrollLeft += e.deltaY;
        }
    };

    // Set up scroll listeners and reset scroll position - only when component should render
    useEffect(() => {
        if (!shouldRender) return;
        
        const container = scrollContainerRef.current;
        if (!container) return;

        let isMounted = true;

        // Force scroll to beginning - use setTimeout to ensure DOM is ready
        const resetScroll = () => {
            if (!isMounted || !container) return;
            container.scrollLeft = 0;
            updateScrollButtons();
        };
        
        // Reset immediately and also after a short delay to handle browser scroll restoration
        resetScroll();
        const timeoutId = setTimeout(() => {
            if (isMounted) resetScroll();
        }, 100);

        // Add event listeners
        const handleScrollUpdate = () => {
            if (isMounted) updateScrollButtons();
        };
        
        const handleWheelUpdate = (e) => {
            if (isMounted) handleWheel(e);
        };

        const handleResize = () => {
            if (isMounted) updateScrollButtons();
        };

        container.addEventListener('scroll', handleScrollUpdate);
        container.addEventListener('wheel', handleWheelUpdate, { passive: false });
        window.addEventListener('resize', handleResize);

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
            if (container) {
                container.removeEventListener('scroll', handleScrollUpdate);
                container.removeEventListener('wheel', handleWheelUpdate);
            }
            window.removeEventListener('resize', handleResize);
        };
    }, [shouldRender, specialsList]);

    // Early return if component shouldn't render
    if (!shouldRender) {
        return null;
    }

    return (
        <div className={`headless-specials-mobile-wrapper ${!defaultDesktopSpecials ? 'hide-on-desktop' : ''}`}>
            <div className='specials-mobile-wrapper-title'>
                <span className="mobile-title">Current {safeSpecialsName}</span>
                <span className="desktop-title">{desktopSpecialsTitle || `Current ${safeSpecialsName}`}</span>
            </div>
            <div className='specials-mobile-wrapper-in'>
                {showScrollButtons && (
                    <button
                        className={`headless-specials-scroll-button headless-specials-scroll-left ${!canScrollLeft ? 'disabled' : ''}`}
                        onClick={() => scrollTo('left')}
                        disabled={!canScrollLeft}
                        aria-label="Scroll left"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                        </svg>
                    </button>
                )}

                <div
                    className="headless-specials-mobile"
                    ref={scrollContainerRef}
                >
                    {specialsList.map((special) => (
                        <Special
                            key={special.id}
                            special={special}
                            singleSpecialView={false}
                            setSelectedSpecialId={setSelectedSpecialId}
                            menuSpecials={true}
                        />
                    ))}
                </div>

                {showScrollButtons && (
                    <button
                        className={`headless-specials-scroll-button headless-specials-scroll-right ${!canScrollRight ? 'disabled' : ''}`}
                        onClick={() => scrollTo('right')}
                        disabled={!canScrollRight}
                        aria-label="Scroll right"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
});

export default MobileSpecials;
